import type { CSSProperties } from "react";
import { PayStatusButton } from "./pay-status";
import {
  CONTROL_HEIGHT,
  Field,
  SHIMMER_KEYFRAMES,
  button,
  shimmer,
  size,
  text,
} from "./ui";
import { Divider, Stack, TERMINAL } from "./layout";

/**
 * The checkout before its pieces exist, built from the same parts and
 * spacing as the real ones, so everything loads into it without the frame
 * changing height (a sheet around it follows that height, so a jump would
 * move it).
 */

/** Outside host mode, the order card and the address field while the
 *  session loads, in the same places the real ones take. */
export function SummaryShape() {
  return (
    <div
      aria-hidden="true"
      data-lc-shimmer=""
      style={{ ...shimmer("var(--lc-radius)"), height: 64 }}
    />
  );
}

export function HostCardShape({ payLabel }: { payLabel?: string } = {}) {
  const piece = (height: number, radius?: string): CSSProperties => ({
    ...shimmer(radius),
    height,
  });
  return (
    <Stack gap={1.5} grow>
      <style>{SHIMMER_KEYFRAMES}</style>
      <div style={{ display: "grid", gap: "calc(var(--lc-space) * 1.5)" }}>
        <div
          aria-hidden="true"
          data-lc-shimmer=""
          style={piece(CONTROL_HEIGHT, "var(--lc-button-radius)")}
        />
        {/* Held in place, unseen, until a wallet is known to be there. */}
        <div style={{ visibility: "hidden" }}>
          <Divider>Or pay with card</Divider>
        </div>
      </div>
      <Stack gap={1.5} grow>
        <div aria-busy="true" aria-label="Loading card entry">
          <Field label={null}>
            <div
              style={{ display: "grid", gap: "calc(var(--lc-space) * 0.75)" }}
            >
              <div data-lc-shimmer="" style={piece(CONTROL_HEIGHT)} />
              <div
                style={{
                  display: "grid",
                  gap: "calc(var(--lc-space) * 0.75)",
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <div data-lc-shimmer="" style={piece(CONTROL_HEIGHT)} />
                <div data-lc-shimmer="" style={piece(CONTROL_HEIGHT)} />
              </div>
            </div>
          </Field>
        </div>
        <div style={TERMINAL}>
          {payLabel ? (
            // What it will cost is already known: the button, not its shape,
            // saying what it has since Continue.
            <PayStatusButton
              status={null}
              phase={null}
              idle={payLabel}
              loading
              disabled
              onClick={() => undefined}
              style={{ ...button.primary, minHeight: CONTROL_HEIGHT }}
            />
          ) : (
            <div
              aria-hidden="true"
              data-lc-shimmer=""
              style={piece(CONTROL_HEIGHT, "var(--lc-button-radius)")}
            />
          )}
        </div>
      </Stack>
    </Stack>
  );
}

/** The destination row before the session exists: its label, and the chip's
 *  place shimmering. */
export function DestinationShape() {
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        gap: "calc(var(--lc-space) * 1.5)",
        justifyContent: "space-between",
      }}
    >
      <span
        style={{ ...text.strong, fontSize: size.small, whiteSpace: "nowrap" }}
      >
        Tickets destination
      </span>
      <span
        aria-hidden="true"
        data-lc-shimmer=""
        style={{ ...shimmer("999px"), height: 28, width: 190 }}
      />
    </div>
  );
}
